/**
 * Helper functions for ClassModal component
 */

/**
 * Check if the class time has already passed
 * @param {Date|string} classTime - The class time
 * @returns {boolean} - True if current time is past the class time
 */
export const isClassTimePassed = (classTime) => {
    const classDateTime = new Date(classTime);
    const currentTime = new Date();
    return currentTime > classDateTime;
};

/**
 * Check if the class is over (considering cancel buffer)
 * @param {Date|string} classTime - The class time
 * @param {number} cancelBeforeHours - Hours before class when cancellation is not allowed
 * @returns {boolean} - True if it's too late to cancel
 */
export const isClassOver = (classTime, cancelBeforeHours = 1) => {
    const classDateTime = new Date(classTime);
    const currentTime = new Date();
    
    // Convert hours to milliseconds
    const bufferInMs = 60 * 60 * 1000 * cancelBeforeHours;
    
    // Class is "over" if it's less than buffer time until class starts or already started
    return classDateTime.getTime() - currentTime.getTime() < bufferInMs;
};

/**
 * Get color for flag based on flag type
 * @param {string} flag - The flag type (red, yellow, green, blue)
 * @returns {string} - The color code
 */
export const getFlagColor = (flag) => {
    const flagColors = {
        'red': '#f44336',
        'yellow': '#ffeb3b',
        'green': '#4caf50',
        'blue': '#2196f3'
    };
    return flagColors[flag?.toLowerCase()] || '#757575';
};

/**
 * Check if user is within registration period
 * @param {Date|string} classTime - The class time
 * @param {number} registrationPeriod - Hours before class when registration opens
 * @returns {boolean} - True if user can register now
 */
export const isWithinRegistrationPeriod = (classTime, registrationPeriod) => {
    if (!registrationPeriod) return true;
    
    const classDateTime = new Date(classTime);
    const currentTime = new Date();
    const hoursUntilClass = (classDateTime.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
    
    return hoursUntilClass <= registrationPeriod;
};

/**
 * Format class time for display in Estonian timezone
 * @param {Date|string} classTime - The class time
 * @param {string} locale - Locale for formatting (default: 'et-EE')
 * @returns {string} - Formatted time string
 */
export const formatClassTime = (classTime, locale = 'et-EE') => {
    return new Date(classTime).toLocaleString(locale, {
        timeZone: 'Europe/Tallinn',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format class time (only time, no date) in Estonian timezone
 * @param {Date|string} classTime - The class time
 * @param {string} locale - Locale for formatting (default: 'et-EE')
 * @returns {string} - Formatted time string (HH:MM)
 */
export const formatClassTimeOnly = (classTime, locale = 'et-EE') => {
    return new Date(classTime).toLocaleTimeString(locale, {
        timeZone: 'Europe/Tallinn',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format class date (only date, no time) in Estonian timezone
 * @param {Date|string} classTime - The class time
 * @param {string} locale - Locale for formatting (default: 'et-EE')
 * @param {boolean} includeYear - Whether to include year (default: false for short format)
 * @returns {string} - Formatted date string
 */
export const formatClassDate = (classTime, locale = 'et-EE', includeYear = false) => {
    const options = {
        timeZone: 'Europe/Tallinn',
        day: '2-digit',
        month: '2-digit'
    };
    if (includeYear) {
        options.year = 'numeric';
    }
    return new Date(classTime).toLocaleDateString(locale, options);
};

/**
 * Check if all attendees are checked in
 * @param {Array} attendees - Array of attendee objects
 * @returns {boolean} - True if all are checked in
 */
export const allAttendeesCheckedIn = (attendees) => {
    if (!Array.isArray(attendees) || attendees.length === 0) return false;
    return attendees.every(attendee => attendee.checkIn);
};

const SESSION_EXHAUSTED_GRACE_MS = 14 * 24 * 60 * 60 * 1000;
const CONTRACT_END_GRACE_MS = 7 * 24 * 60 * 60 * 1000;

const getPlanEndOfDayTime = (plan) => {
    if (!plan?.endDate) {
        return null;
    }
    const end = new Date(plan.endDate);
    end.setHours(23, 59, 59, 999);
    return end.getTime();
};

export const planHasRemainingSessions = (plan) =>
    plan?.sessionsLeft > 0 || plan?.sessionsLeft === 9999;

/**
 * Plan is currently usable (sessions left and not past endDate).
 * Contract plans get a 7-day grace after endDate.
 */
export const isActiveAffiliatePlan = (plan, now = Date.now()) => {
    if (!planHasRemainingSessions(plan)) {
        return false;
    }

    const endTime = getPlanEndOfDayTime(plan);
    if (endTime == null) {
        return true;
    }

    let expiry = endTime;
    if (plan.contractId != null && plan.contractId > 0) {
        expiry += CONTRACT_END_GRACE_MS;
    }

    return now < expiry;
};

/**
 * Whether a member with an active-plan visibility setting can see the class list.
 * Exhausted session packs stay visible for up to 2 weeks after the final
 * session was used, but never beyond userPlan.endDate.
 */
export const canSeeParticipantsWithPlan = (plan, classTime, now = Date.now()) => {
    if (planHasRemainingSessions(plan)) {
        return isActiveAffiliatePlan(plan, now);
    }

    const endTime = getPlanEndOfDayTime(plan);
    const exhaustedAt = new Date(plan?.sessionsExhaustedAt).getTime();
    const selectedClassTime = new Date(classTime).getTime();
    if (
        endTime == null ||
        Number.isNaN(exhaustedAt) ||
        Number.isNaN(selectedClassTime)
    ) {
        return false;
    }

    const visibilityCutoff = Math.min(
        exhaustedAt + SESSION_EXHAUSTED_GRACE_MS,
        endTime
    );
    return now < visibilityCutoff && selectedClassTime <= visibilityCutoff;
};

